import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, http } from "@/lib/api";

export function ReportEmailDialog({ row, open, onOpenChange, onSent }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setEmail(row?.contact_email || ""); }, [open, row]);
  if (!row) return null;

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/brands/${row.id}/send-report`, { email });
      toast.success(`Report sent to ${email}`, { description: `${row.name} · last 30 days` });
      onSent(row.id, email, data?.sent_at);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="report-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-ph-ink">Email {row.name} its report</DialogTitle>
          <DialogDescription>Sends the last 30 days of spotlight views, clicks and click rate. The address is saved privately for next time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["Views", row.views_30d], ["Clicks", row.clicks_30d], ["Rate", row.views_30d ? `${Math.min(100, (row.clicks_30d / row.views_30d) * 100).toFixed(1)}%` : "—"]].map(([l, v]) => (
            <div key={l} className="rounded-2xl bg-ph-raised py-3"><p className="font-mono-ph text-[10px] uppercase tracking-wider text-ph-muted">{l}</p><p className="font-display text-2xl font-extrabold text-ph-ink">{v}</p></div>
          ))}
        </div>
        <form onSubmit={send} className="space-y-3">
          <label htmlFor="report-email" className="block text-sm font-medium text-ph-ink/85">Brand contact email</label>
          <input id="report-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="marketing@brand.com" data-testid="report-email-input"
            className="h-12 w-full rounded-2xl border border-ph-line px-4 text-sm outline-none focus:border-ph-accent/60 focus:shadow-[0_0_0_4px_rgba(37,99,235,0.1)]" />
          <button type="submit" disabled={busy} data-testid="report-email-send"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ph-lime font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send report
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
